import { SW_API_CACHE_PREFIX, SW_CACHE_RESET_MESSAGE } from "./swCacheNames.js";

/**
 * How long to wait for the worker to confirm it reset. Short: the fallback below
 * is the behaviour this app had before the worker was involved at all, so a
 * worker that is wedged, or an old one still controlling the page mid-deploy
 * that has no reset handler, costs a moment rather than a hung sign-out.
 */
export const RESET_ACK_TIMEOUT_MS = 1500;

/**
 * Ask the service worker to reset the API cache, and wait for it to confirm.
 *
 * Deleting the cache from here cannot be ordered against work already queued
 * inside the worker (codex review): a read issued under the old household can
 * resolve, and write, in the gap between `postMessage` queueing a task and the
 * worker running it. So the worker performs BOTH the epoch bump and the delete
 * — bump first so later continuations are refused, delete second so an earlier
 * continuation's write is wiped — and replies when they are done.
 *
 * Returns false when there is no controlling worker to ask, or it did not
 * answer, in which case the caller falls back to deleting the cache directly.
 */
function askControllerToReset(): Promise<boolean> {
  try {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return Promise.resolve(false);
    const controller = navigator.serviceWorker.controller;
    if (!controller || typeof MessageChannel === "undefined") return Promise.resolve(false);
    return new Promise<boolean>((resolve) => {
      const channel = new MessageChannel();
      const timer = setTimeout(() => resolve(false), RESET_ACK_TIMEOUT_MS);
      const settle = (value: boolean) => {
        clearTimeout(timer);
        resolve(value);
      };
      channel.port1.onmessage = (event: MessageEvent) => settle((event.data as { ok?: boolean } | null)?.ok === true);
      controller.postMessage({ type: SW_CACHE_RESET_MESSAGE }, [channel.port2]);
    });
  } catch {
    // Messaging unavailable; the direct delete still runs.
    return Promise.resolve(false);
  }
}

/** How long to wait for a newly installed worker to take control of the page. */
export const TAKEOVER_TIMEOUT_MS = 3000;

/**
 * Pull in a worker that knows how to reset, when the one controlling this page
 * does not (codex review).
 *
 * This is the deployment window: a client is still controlled by the previous
 * worker, which has no reset handler and no epoch guard, so deleting the cache
 * from here cannot stop that worker re-populating it from a read it already had
 * in flight. Nothing shipped in the new worker can fix the old one — but the new
 * one can be made to take over. It calls `skipWaiting()` on install and
 * `clients.claim()` on activate, so once `update()` finds it, control changes
 * without a reload and the reset can be asked for again.
 *
 * False when there is nothing newer to adopt (the ordinary case, and offline,
 * where `update()` cannot fetch anything).
 */
async function adoptResetCapableWorker(): Promise<boolean> {
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) return false;
    const tookOver = new Promise<boolean>((resolve) => {
      const timer = setTimeout(() => resolve(false), TAKEOVER_TIMEOUT_MS);
      navigator.serviceWorker.addEventListener("controllerchange", () => {
        clearTimeout(timer);
        resolve(true);
      }, { once: true });
    });
    await registration.update().catch(() => undefined);
    return await tookOver;
  } catch {
    return false;
  }
}

/**
 * Get the API cache reset done by a worker that can order it correctly, or say
 * it could not be.
 */
async function requestWorkerReset(): Promise<boolean> {
  if (await askControllerToReset()) return true;
  // No acknowledgement: either nothing controls this page, or what does predates
  // the reset protocol. Only the second case is fixable, and adopting the new
  // worker is how.
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return false;
  if (!navigator.serviceWorker.controller) return false;
  if (!(await adoptResetCapableWorker())) return false;
  return askControllerToReset();
}

/**
 * Purge the service worker's offline API response cache (#1003).
 *
 * The cache is keyed by request URL alone. Household scope is carried by the
 * session server-side, never in the URL, so household A's cached
 * `GET /api/v1/assets` is indistinguishable from household B's — and `sw.js`
 * falls back to it on *any* fetch failure, not just a declared offline state.
 *
 * Every transition that changes whose data this browser may see therefore has
 * to purge it, or the next network hiccup serves the new context the old
 * context's data. There are two such transitions and both call this: signing
 * out, and switching active household.
 */
export async function purgeOfflineApiCache(): Promise<void> {
  // Preferred: the worker resets itself and tells us when it is done, which is
  // the only ordering that also covers reads it already had in flight.
  if (await requestWorkerReset()) return;
  // Fallback for an uncontrolled page, a worker too old to know the message
  // (the deploy window), or one that did not answer. Absent on insecure origins
  // and in the unit-test environment — and a context without a CacheStorage has
  // nothing cached to leak either.
  if (typeof caches === "undefined") return;
  try {
    // EVERY API cache, not just the current name (codex review). This branch is
    // reached precisely when an old worker is still in charge — it cannot be
    // adopted, or nothing answered — and that worker reads and writes the
    // PREVIOUS name. Deleting only the current one would leave the previous
    // household's responses sitting in `household-api-v1` with a live worker
    // still serving them, which is the leak this whole change is about.
    const names = await caches.keys();
    await Promise.all(
      names.filter((name) => name.startsWith(SW_API_CACHE_PREFIX)).map((name) => caches.delete(name))
    );
  } catch {
    // Storage evicted the cache from under us, or the profile denies it. The
    // caller is mid-teardown and has no better answer than continuing; the
    // worker re-creates the cache from scratch on the next successful read.
  }
  // KNOWN RESIDUAL, deliberately not closed here (codex review, #1003).
  //
  // Reaching this line at all means no worker would own the reset: a legacy
  // controller that never acknowledged and could not be replaced, which offline
  // is the ordinary outcome because `update()` cannot fetch. That worker has no
  // epoch guard, so a GET it already had in flight can resolve after the sweep
  // above and `cache.put` the previous household's body back into v1 — and the
  // same worker serves it on the next network failure. The sweep removes what
  // exists when it runs; it cannot order against writes from a worker this page
  // does not control.
  //
  // Nothing callable from here closes that. The only reliable end is to stop
  // that worker controlling the page, which means a forced reload — and a
  // reload on the session-less boot path can loop, so it needs a one-shot guard
  // and is a UX decision rather than a tuning one. Filed on #1003.
  //
  // The window is one deploy wide and self-heals: the moment the new worker
  // does activate, its `activate` cleanup deletes v1 outright.
}

/**
 * Sign out, then tear down local state — with the teardown guaranteed to run
 * even when the logout request never reaches the server (#1003).
 *
 * The teardown is what clears this profile's household data, including the
 * offline API cache above. Awaiting the request first and tearing down after is
 * the right *order* (a server that rejects the sign-out should not be told
 * about it later), but making the teardown conditional on that request
 * succeeding is what left the previous user's responses cached on a shared
 * device whenever sign-out was tapped offline, through a tunnel that was down,
 * or against a 5xx.
 *
 * The teardown is *awaited*, not fired and forgotten (codex review). The
 * caller's `run()` resolves as soon as this does and the signed-out UI is on
 * screen immediately after, so a purge still in flight at that point is a purge
 * the next session can race: one network failure before `CacheStorage.delete`
 * settles and `sw.js` answers the new session out of the old one's cache.
 *
 * A logout rejection is deliberately re-thrown, but only after the teardown has
 * finished — the caller's `run()` surfaces it, so the user is told the
 * server-side session may still be alive rather than being shown a clean
 * sign-out that only half happened.
 */
export async function signOutWithTeardown(
  logout: () => Promise<unknown>,
  teardown: () => void | Promise<void>
): Promise<void> {
  let failure: unknown = null;
  try {
    await logout();
  } catch (error) {
    failure = error;
  }
  await teardown();
  if (failure !== null) throw failure;
}
